import React, { useState, useEffect, useCallback, useRef } from 'react';
import './TimestampFilter.scss';

export interface TimestampFilterProps {
  minTimestamp: number;
  maxTimestamp: number;
  fromTimestamp: number;
  toTimestamp: number;
  onRangeChange: (from: number, to: number) => void;
  onFollowModeChange?: (followMode: boolean) => void;
}

function formatTimestamp(timestamp: number, includeDate: boolean): string {
  const date = new Date(timestamp);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  const timeStr = `${h}:${m}:${s}`;

  if (includeDate) {
    return `${month}/${day} ${timeStr}`;
  }
  return timeStr;
}

function isSameDay(ts1: number, ts2: number): boolean {
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

export const TimestampFilter: React.FC<TimestampFilterProps> = ({
  minTimestamp,
  maxTimestamp,
  fromTimestamp,
  toTimestamp,
  onRangeChange,
  onFollowModeChange,
}) => {
  const [dragging, setDragging] = useState<'from' | 'to' | null>(null);
  const [stickyToEnd, setStickyToEnd] = useState(true); // Start sticky by default
  const trackRef = useRef<HTMLDivElement>(null);

  const range = maxTimestamp - minTimestamp;
  const fromPercent = range > 0 ? ((fromTimestamp - minTimestamp) / range) * 100 : 0;
  const toPercent = range > 0 ? ((toTimestamp - minTimestamp) / range) * 100 : 100;

  // Auto-expand TO slider when sticky and maxTimestamp increases
  useEffect(() => {
    if (stickyToEnd && toTimestamp < maxTimestamp) {
      onRangeChange(fromTimestamp, maxTimestamp);
    }
  }, [maxTimestamp, stickyToEnd, fromTimestamp, toTimestamp, onRangeChange]);

  // Notify parent when follow mode changes
  useEffect(() => {
    if (onFollowModeChange) {
      onFollowModeChange(stickyToEnd);
    }
  }, [stickyToEnd, onFollowModeChange]);

  const handleMove = useCallback((clientX: number) => {
    if (!trackRef.current || !dragging) return;
    const rect = trackRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const ts = minTimestamp + (percent / 100) * range;

    if (dragging === 'from') {
      onRangeChange(Math.min(ts, toTimestamp), toTimestamp);
    } else {
      // Dragging the TO slider
      const newTo = Math.max(ts, fromTimestamp);
      onRangeChange(fromTimestamp, newTo);

      // Check if user dragged all the way to the right edge (within 2% of track width)
      if (percent >= 98) {
        setStickyToEnd(true);
      } else {
        setStickyToEnd(false);
      }
    }
  }, [dragging, minTimestamp, range, fromTimestamp, toTimestamp, onRangeChange]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => { e.preventDefault(); handleMove(e.clientX); };
    const onUp = () => setDragging(null);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragging, handleMove]);

  const spansDays = !isSameDay(minTimestamp, maxTimestamp);

  return (
    <div className="timestamp-filter">
      <div className="timestamp-filter__track" ref={trackRef}>
        <div className="timestamp-filter__range" style={{ left: `${fromPercent}%`, width: `${toPercent - fromPercent}%` }} />
        <div className="timestamp-filter__slider" style={{ left: `${fromPercent}%` }} onMouseDown={() => setDragging('from')} />
        <div className="timestamp-filter__slider" style={{ left: `${toPercent}%` }} onMouseDown={() => setDragging('to')} />
      </div>
      <div className="timestamp-filter__labels">
        <span>{formatTimestamp(fromTimestamp, spansDays)}</span>
        <span>{formatTimestamp(toTimestamp, spansDays)}</span>
      </div>
    </div>
  );
};
